import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import DesktopShell from "./layouts/DesktopShell";
import MobileShell from "./layouts/MobileShell";
import AdminPanel from "./pages/AdminPanel";
import Users from "./pages/Users";
import SoonPlaceholder from "./pages/SoonPlaceholder";
import OperatorHome from "./pages/operator/Home";
import OperatorJobs from "./pages/operator/Jobs";
import OperatorCamera from "./pages/operator/Camera";
import OperatorProfile from "./pages/operator/Profile";
import OperatorHoras from "./pages/operator/Horas";
import { AssetsList, AssetForm, AssetDetail } from "./pages/master/assets";
import {
    OperatorsList,
    OperatorForm,
    OperatorDetail,
    ClientsList,
    ClientForm,
    ClientDetail,
} from "./pages/master/operators";
import { MaterialsList, ChecklistsList } from "./pages/master/misc";
import { JobsList, JobForm, JobDetail } from "./pages/jobs";
import OperatorJobDetail from "./pages/operator/JobDetail";
import OperatorRegistro from "./pages/operator/Registro";
import PublicTrack from "./pages/PublicTrack";
import Verification from "./pages/Verification";
import Maintenance from "./pages/Maintenance";
import Finanzas from "./pages/Finanzas";
import Gastos from "./pages/Gastos";
import Combustible from "./pages/Combustible";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { SuperadminResetRequests, SuperadminWipe, AuditLogs } from "./pages/Superadmin";
import Manuales from "./pages/Manuales";
import ManualOperador from "./pages/ManualOperador";
import Casetas from "./pages/Casetas";
import Calendar from "./pages/Calendar";
import SWUpdateBanner from "./components/SWUpdateBanner";
import dict from "./i18n/es-MX";
import "./App.css";

function FullPageLoader() {
    return (
        <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center">
            <span className="text-sm uppercase tracking-widest">{dict.common.loading}</span>
        </div>
    );
}

function homeFor(role) {
    if (role === "operator") return "/campo";
    return "/panel";
}

function RequireAuth({ children, roles }) {
    const { user, bootstrapping } = useAuth();
    if (bootstrapping) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role) && user.role !== "superadmin") {
        return <Navigate to={homeFor(user.role)} replace />;
    }
    return children;
}

function PublicOnly({ children }) {
    const { user, bootstrapping } = useAuth();
    if (bootstrapping) return <FullPageLoader />;
    if (user) return <Navigate to={homeFor(user.role)} replace />;
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <SWUpdateBanner />
            <BrowserRouter>
                <Routes>
                    <Route
                        path="/login"
                        element={
                            <PublicOnly>
                                <Login />
                            </PublicOnly>
                        }
                    />

                    {/* Public job tracking — NO auth */}
                    <Route path="/track/:token" element={<PublicTrack />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Admin/Manager desktop shell */}
                    <Route
                        element={
                            <RequireAuth roles={["admin", "manager"]}>
                                <DesktopShell />
                            </RequireAuth>
                        }
                    >
                        <Route path="/panel" element={<AdminPanel />} />
                        <Route path="/verificacion" element={<Verification />} />
                        <Route path="/mantenimiento" element={<Maintenance />} />

                        {/* Phase 2 — Master Data */}
                        <Route path="/activos" element={<AssetsList />} />
                        <Route path="/activos/nuevo" element={<AssetForm />} />
                        <Route path="/activos/:id" element={<AssetDetail />} />
                        <Route path="/activos/:id/editar" element={<AssetForm />} />

                        <Route path="/operadores" element={<OperatorsList />} />
                        <Route path="/operadores/nuevo" element={<OperatorForm />} />
                        <Route path="/operadores/:id" element={<OperatorDetail />} />
                        <Route path="/operadores/:id/editar" element={<OperatorForm />} />

                        <Route path="/clientes" element={<ClientsList />} />
                        <Route path="/clientes/nuevo" element={<ClientForm />} />
                        <Route path="/clientes/:id" element={<ClientDetail />} />
                        <Route path="/clientes/:id/editar" element={<ClientForm />} />

                        <Route path="/materiales" element={<MaterialsList />} />

                        {/* Phase 3 — Jobs */}
                        <Route path="/trabajos" element={<JobsList />} />
                        <Route path="/trabajos/nuevo" element={<JobForm />} />
                        <Route path="/trabajos/:id" element={<JobDetail />} />
                        <Route path="/trabajos/:id/editar" element={<JobForm />} />

                        {/* Phase 8F — Calendario (admin + manager only) */}
                        <Route
                            path="/calendario"
                            element={
                                <RequireAuth roles={["admin", "manager"]}>
                                    <Calendar />
                                </RequireAuth>
                            }
                        />

                        {/* Phase 6 — Finanzas */}
                        <Route
                            path="/finanzas"
                            element={
                                <RequireAuth roles={["admin", "manager"]}>
                                    <Finanzas />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/gastos"
                            element={
                                <RequireAuth roles={["admin", "manager"]}>
                                    <Gastos />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/casetas"
                            element={
                                <RequireAuth roles={["admin", "manager"]}>
                                    <Casetas />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/combustible"
                            element={
                                <RequireAuth roles={["admin", "manager"]}>
                                    <Combustible />
                                </RequireAuth>
                            }
                        />
                        {/* Phase 11 — Audit + Superadmin sections */}
                        <Route
                            path="/auditoria"
                            element={
                                <RequireAuth roles={["admin", "manager"]}>
                                    <AuditLogs />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/superadmin/reset-requests"
                            element={
                                <RequireAuth roles={["superadmin"]}>
                                    <SuperadminResetRequests />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/superadmin/wipe"
                            element={
                                <RequireAuth roles={["superadmin"]}>
                                    <SuperadminWipe />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/manuales"
                            element={
                                <RequireAuth roles={["superadmin"]}>
                                    <Manuales />
                                </RequireAuth>
                            }
                        />

                        <Route
                            path="/usuarios"
                            element={
                                <RequireAuth roles={["admin"]}>
                                    <Users />
                                </RequireAuth>
                            }
                        />

                        {/* Admin-only settings */}
                        <Route
                            path="/configuracion"
                            element={
                                <RequireAuth roles={["admin"]}>
                                    <Navigate to="/configuracion/checklists" replace />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/configuracion/checklists"
                            element={
                                <RequireAuth roles={["admin"]}>
                                    <ChecklistsList />
                                </RequireAuth>
                            }
                        />
                    </Route>

                    {/* Operator mobile shell */}
                    <Route
                        element={
                            <RequireAuth roles={["operator"]}>
                                <MobileShell />
                            </RequireAuth>
                        }
                    >
                        <Route path="/campo" element={<OperatorHome />} />
                        <Route path="/campo/trabajos" element={<OperatorJobs />} />
                        <Route path="/campo/trabajos/:id" element={<OperatorJobDetail />} />
                        <Route path="/campo/trabajos/:id/registro" element={<OperatorRegistro />} />
                        <Route path="/campo/camara" element={<OperatorCamera />} />
                        <Route path="/campo/horas" element={<OperatorHoras />} />
                        <Route path="/campo/perfil" element={<OperatorProfile />} />
                    </Route>

                    {/* Full-screen operator manual (any authenticated user) */}
                    <Route
                        path="/manual/operador"
                        element={
                            <RequireAuth>
                                <ManualOperador />
                            </RequireAuth>
                        }
                    />

                    {/* Backwards compat */}
                    <Route path="/dashboard" element={<Navigate to="/panel" replace />} />
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
